import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { desfazerAlteracao, listarAlteracoes, type Alteracao } from '../api/alteracoes'
import { useAuth } from '../state/AuthContext'
import { useFiltros } from '../state/FiltrosContext'
import { nomePlataforma } from '../api/trafegoPago'
import { descreverTipo, formatarValor, nomeNivel, PLATAFORMAS_ALTERAVEIS } from '../utils/alteracoes'

const SITUACAO: Record<Alteracao['status'], { texto: string; cor: 'success' | 'error' | 'default' | 'warning' }> = {
  aplicada: { texto: 'Aplicada', cor: 'success' },
  desfeita: { texto: 'Desfeita', cor: 'default' },
  falhou: { texto: 'Falhou', cor: 'error' },
  pendente: { texto: 'Em andamento', cor: 'warning' },
}
const ORIGEM: Record<Alteracao['origem'], string> = { sugestao: 'Pela sugestão', manual: 'Manual', desfazer: 'Desfazer' }
const quando = (iso: string) =>
  new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(
    new Date(iso),
  )

/**
 * Registro de tudo que o sistema mudou nas campanhas: quem, quando, o antes e o depois de
 * cada item. Quem vê os painéis da marca vê o registro; desfazer é de quem tem campanhas.alterar.
 */
export function AlteracoesPage() {
  const { pode } = useAuth()
  const { marcaId } = useFiltros()
  const [lista, setLista] = useState<Alteracao[] | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [confirmar, setConfirmar] = useState<Alteracao | null>(null)
  const [desfazendo, setDesfazendo] = useState(false)
  const podeDesfazer = pode('campanhas.alterar')

  const carregar = useCallback(async () => {
    try {
      setLista(await listarAlteracoes(marcaId || null))
    } catch (e) {
      setErro((e as Error).message)
    }
  }, [marcaId])

  useEffect(() => {
    setLista(null)
    carregar()
  }, [carregar])

  const desfazer = async () => {
    if (!confirmar) return
    setDesfazendo(true)
    setErro(null)
    try {
      await desfazerAlteracao(confirmar.id)
      setConfirmar(null)
      await carregar()
    } catch (e) {
      setConfirmar(null)
      setErro((e as Error).message)
    } finally {
      setDesfazendo(false)
    }
  }

  return (
    <Stack spacing={2}>
      <Box sx={{ maxWidth: 760 }}>
        <Typography variant="h5" sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, lineHeight: 1.2 }}>
          Alterações nas campanhas
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Tudo o que foi pausado, reativado ou teve o orçamento ajustado pelo sistema, com quem confirmou e o valor de antes. Nada muda sozinho:
          cada linha é um clique em “Aplicar”. Desfazer volta o valor de antes, se ninguém tiver mexido nele depois, direto no Meta ou no Google Ads.
        </Typography>
      </Box>

      {erro ? (
        <Alert severity="error" onClose={() => setErro(null)}>
          {erro}
        </Alert>
      ) : null}

      {!lista ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !lista.length ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography color="text.secondary">
            Nenhuma alteração ainda. Elas são feitas pelo botão “Aplicar” nas sugestões do Tráfego pago, ou pelo menu ⋯ de cada anúncio e conjunto
            em “Por dentro das campanhas”.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {lista.map((a) => {
            const s = SITUACAO[a.status]
            const desfazivel = podeDesfazer && a.status === 'aplicada' && a.origem !== 'desfazer'
            return (
              <Paper key={a.id} variant="outlined" sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 0.5 }}>
                      <Chip size="small" variant="outlined" color={s.cor} label={s.texto} />
                      <Typography variant="caption" color="text.secondary">
                        {quando(a.criadaEm)} · {a.email} · {a.marca} · {nomePlataforma(a.plataforma)} · {ORIGEM[a.origem]}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                      {a.origem === 'desfazer' ? 'Desfez: ' : ''}
                      {descreverTipo(a.tipo, a.percentual)} · {nomeNivel(a.nivel, a.plataforma).toLowerCase()} {a.nome}
                    </Typography>
                    <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                      {a.itens.map((i) => (
                        <Typography key={`${i.id_externo}-${i.campo}`} component="li" variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                          {a.itens.length > 1 ? `${i.nome}: ` : ''}
                          {formatarValor(i, i.antes, a.moeda)} → {formatarValor(i, i.depois, a.moeda)}
                          {a.status === 'falhou' && i.aplicado ? ' (ficou aplicado)' : ''}
                        </Typography>
                      ))}
                    </Box>
                    {a.motivo ? (
                      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
                        Sugestão: {a.motivo}
                      </Typography>
                    ) : null}
                    {a.erro ? (
                      <Typography variant="caption" color="error" component="p" sx={{ mt: 0.5 }}>
                        {a.erro}
                      </Typography>
                    ) : null}
                    {a.desfeitaEm ? (
                      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
                        Desfeita em {quando(a.desfeitaEm)}.
                      </Typography>
                    ) : null}
                  </Box>
                  {desfazivel ? (
                    <Button size="small" variant="outlined" onClick={() => setConfirmar(a)} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                      Desfazer
                    </Button>
                  ) : null}
                </Stack>
              </Paper>
            )
          })}
        </Stack>
      )}

      <Dialog open={!!confirmar} onClose={desfazendo ? undefined : () => setConfirmar(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Desfazer a alteração?</DialogTitle>
        <DialogContent>
          {confirmar ? (
            <Typography variant="body2">
              {confirmar.itens.map((i) => `${i.nome}: volta para ${formatarValor(i, i.antes, confirmar.moeda)}`).join(' · ')}. A mudança vai direto para o {nomePlataforma(confirmar.plataforma)} e também
              fica no registro.
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmar(null)} disabled={desfazendo} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={desfazer} disabled={desfazendo} sx={{ textTransform: 'none' }}>
            {desfazendo ? 'Desfazendo…' : `Desfazer no ${PLATAFORMAS_ALTERAVEIS[confirmar?.plataforma ?? ''] ?? 'plataforma'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}
